'use client'
import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@payloadcms/ui'

// ── Icons ──────────────────────────────────────────────────────────────────

const Icon = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      width: 18,
      height: 18,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {children}
  </span>
)

const TreatmentsIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10z" />
      <path d="M8 12h8M12 8v8" />
    </svg>
  </Icon>
)

const FileIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  </Icon>
)

const GridIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  </Icon>
)

const UsersIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  </Icon>
)

const ImageIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  </Icon>
)

const PlayIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="m9 10 5 3-5 3V10z" />
    </svg>
  </Icon>
)

const BookIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  </Icon>
)

const SettingsIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  </Icon>
)

const EditIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  </Icon>
)

const LogoutIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  </Icon>
)

const UserIcon = () => (
  <Icon>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  </Icon>
)

// ── Nav structure ──────────────────────────────────────────────────────────

const groups = [
  {
    label: 'Posts',
    items: [
      { label: 'Instagram', href: '/admin/collections/instagram-posts', icon: <FileIcon /> },
      { label: 'News', href: '/admin/collections/news', icon: <FileIcon /> },
    ],
  },
  {
    label: 'Behandlungen',
    items: [
      { label: 'Behandlungen', href: '/admin/collections/treatments', icon: <TreatmentsIcon /> },
      {
        label: 'Behandlungsdetails',
        href: '/admin/collections/treatment-details',
        icon: <FileIcon />,
      },
      {
        label: 'Behandlungskategorien',
        href: '/admin/collections/treatment-categories',
        icon: <GridIcon />,
      },
    ],
  },
  {
    label: 'Startseite',
    items: [{ label: 'Hero-Slides', href: '/admin/collections/hero-slides', icon: <PlayIcon /> }],
  },
  {
    label: 'Praxis',
    items: [
      { label: 'Team', href: '/admin/collections/team', icon: <UsersIcon /> },
      { label: 'Seiten', href: '/admin/collections/pages', icon: <BookIcon /> },
      { label: 'Medien', href: '/admin/collections/media', icon: <ImageIcon /> },
    ],
  },
  {
    label: 'Einstellungen',
    items: [
      { label: 'Einstellungen', href: '/admin/globals/settings', icon: <SettingsIcon /> },
      { label: 'Seiteninhalt', href: '/admin/globals/site-content', icon: <EditIcon /> },
      { label: 'Benutzer', href: '/admin/collections/users', icon: <UserIcon /> },
    ],
  },
]

// ── Component ──────────────────────────────────────────────────────────────

export default function CustomNav() {
  const pathname = usePathname()
  const { user, logOut } = useAuth()

  const isActive = (href: string) => pathname?.startsWith(href) ?? false

  return (
    <nav
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        padding: '0',
        background: 'var(--theme-elevation-0)',
      }}
    >
      {/* Logo */}
      <Link
        href="/admin"
        style={{
          display: 'block',
          padding: '28px 20px 24px',
          textDecoration: 'none',
          borderBottom: '1px solid var(--theme-border-color)',
          marginBottom: 8,
        }}
      >
        <p
          style={{
            fontFamily: 'serif',
            fontWeight: 300,
            letterSpacing: '0.2em',
            fontSize: 15,
            color: 'var(--theme-text)',
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          KURPARK
        </p>
        <p
          style={{
            fontFamily: 'sans-serif',
            fontWeight: 300,
            letterSpacing: '0.3em',
            fontSize: 9,
            textTransform: 'uppercase',
            color: 'var(--theme-text-dim)',
            margin: 0,
            marginTop: 2,
          }}
        >
          AESTHETICS · CMS
        </p>
      </Link>

      {/* Groups */}
      <div style={{ flex: 1, padding: '4px 12px' }}>
        {groups.map((group) => (
          <div key={group.label} style={{ marginBottom: 20 }}>
            <p
              style={{
                fontFamily: 'sans-serif',
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'var(--theme-text-dim)',
                padding: '6px 8px 4px',
                margin: 0,
              }}
            >
              {group.label}
            </p>
            {group.items.map((item) => {
              const active = isActive(item.href)
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 5,
                    textDecoration: 'none',
                    fontSize: 13.5,
                    fontFamily: 'sans-serif',
                    fontWeight: active ? 500 : 400,
                    color: active ? 'var(--theme-success-500, #9d7c4e)' : 'var(--theme-text)',
                    background: active ? 'var(--theme-elevation-1)' : 'transparent',
                    transition: 'background 0.15s, color 0.15s',
                    marginBottom: 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      ;(e.currentTarget as HTMLElement).style.background =
                        'var(--theme-elevation-1)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      ;(e.currentTarget as HTMLElement).style.background = 'transparent'
                    }
                  }}
                >
                  <span style={{ opacity: active ? 1 : 0.6 }}>{item.icon}</span>
                  {item.label}
                </Link>
              )
            })}
          </div>
        ))}
      </div>

      {/* Footer: user + logout */}
      {user && (
        <div
          style={{
            borderTop: '1px solid var(--theme-border-color)',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <p
            style={{
              fontSize: 12,
              color: 'var(--theme-text-dim)',
              margin: 0,
              fontFamily: 'sans-serif',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {(user as { email?: string }).email ?? ''}
          </p>
          <button
            onClick={() => logOut()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--theme-text-dim)',
              fontSize: 13,
              fontFamily: 'sans-serif',
              padding: '4px 0',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLElement).style.color = 'var(--theme-text)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLElement).style.color = 'var(--theme-text-dim)'
            }}
          >
            <LogoutIcon />
            Abmelden
          </button>
        </div>
      )}
    </nav>
  )
}
